import React from "react";
import {
  Avatar,
  Box,
  Chip,
  Container,
  Divider,
  Grid,
  Hidden,
  IconButton,
  Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";

import ModalLoadingFull from "../../../../components/modals/ModalLoadingFull";
import {
  ArmyListIconButton,
  ArmyListLink,
  userDisplayName
} from "../EventFunctions";
import PlayerModal from "../PlayerModal";
import OrganizePlayerModal from "../OrganizePlayerModal";
import ListResponsive from "../../../../components/other/ListResponsive";
import OrganizeTeamPlayerModal from "../OrganizeTeamPlayerModal";

import { FiltersMenu, OptionsMenu } from "../SubComponents/EventRosterMenus";
import {
  BulkAddPlayerEmailsModal,
  BulkAddPlayerModal
} from "../SubComponents/EventRosterModals";
import { getSearchProps } from "../../utils";
import {
  BCPChair,
  BCPFilter,
  BCPFilterSlash,
  BCPSquarePlus
} from "../../../../components/icons/bcp/BCPIcons";
import { getEnvironmentVariable } from "../../../../global/environmentVariables";
import { postNotification } from "../../../../components/utilities/Notifications";
import WebSocketIndicator from "../SubComponents/WebSocketIndicator";

const IS_WARMACHINE = getEnvironmentVariable("COMPANY_FLAG") === "wm";

export default function EventRosterDefault(props) {
  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventRoster"
  });

  const {
    organize,
    event,
    authContext,
    playerStats,
    players,
    subLoading,
    setPlayer,
    setTeamPlayer,
    optionsAnchorEl,
    setOptionsAnchorEl,
    setLoading,
    setBulkAddEmailsOpen,
    setBulkAddOpen,
    setRefreshPlayers,
    player,
    armies,
    teamPlayer,
    bulkAddOpen,
    bulkAddEmailsOpen,
    loading,
    filtersAnchorEl,
    setFiltersAnchorEl,
    filteredPlayers,
    setFilteredPlayers,
    filterType,
    setFilterType,
    deletePlayer,
    pSocketStatus,
    tSocketStatus,
    handleWSConnection,
    handlePlayers,
    useWebSockets
  } = props;

  return (
    <Container sx={{ pt: 2, pb: 2 }} maxWidth="lg">
      <Grid container sx={{ gap: 2 }}>
        {organize &&
          playerStats?.length > 0 &&
          playerStats.map((playerStat, index) => {
            return (
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  maxWidth: ["100%", "100%", "calc(50% - 8px)"], // deal with spacing bug
                  ml: [1, 0],
                  mr: [1, 0]
                }}
              >
                <Grid
                  container
                  sx={{
                    border: "1px solid rgba(0, 0, 0, 0.12)",
                    borderRadius: 1,
                    boxShadow: 1
                  }}
                >
                  <Grid
                    item
                    xs={8}
                    textAlign="center"
                    sx={{
                      p: 2
                    }}
                  >
                    <Typography variant="h5">{playerStat.label}</Typography>
                  </Grid>
                  <Grid
                    item
                    xs={4}
                    textAlign="center"
                    sx={{
                      p: 2,
                      backgroundColor: `${playerStat.color}.main`,
                      color: `${playerStat.color}.contrastText`,
                      borderTopRightRadius: 1,
                      borderBottomRightRadius: 1
                    }}
                  >
                    <Typography variant="h4">{playerStat.value}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            );
          })}

        {useWebSockets && (
          <Grid item xs={12}>
            <WebSocketIndicator
              status={
                (pSocketStatus === "Live" &&
                  !event?.doublesEvent &&
                  !event?.teamEvent) ||
                (pSocketStatus === "Live" &&
                  tSocketStatus === "Live" &&
                  (event?.doublesEvent || event?.teamEvent))
                  ? "Live"
                  : (pSocketStatus === "Connecting" &&
                        !event?.doublesEvent &&
                        !event?.teamEvent) ||
                      ((pSocketStatus === "Connecting" ||
                        tSocketStatus === "Connecting") &&
                        (event?.doublesEvent || event?.teamEvent))
                    ? "Connecting"
                    : !pSocketStatus && !tSocketStatus
                      ? ""
                      : "Disconnected"
              }
              label
              onClick={async () => {
                if (
                  ((event?.doublesEvent || event?.teamEvent) &&
                    tSocketStatus === "Disconnected") ||
                  pSocketStatus === "Disconnected"
                ) {
                  try {
                    setLoading(true);
                    await handlePlayers();
                    await handleWSConnection();
                  } catch (err) {
                    postNotification("error", err);
                  } finally {
                    setLoading(false);
                  }
                }
              }}
            />
          </Grid>
        )}

        <Grid
          item
          xs={12}
          sx={{
            p: 2
          }}
        >
          <ListResponsive
            originalList={
              subLoading ? null : filteredPlayers ? filteredPlayers : players
            }
            doSearch
            doPages
            rowsPerPageArray={[
              16,
              32,
              48,
              64,
              96,
              { label: t("all"), value: -1 }
            ]}
            initialRowsPerPage={32}
            userRowsPerPage="roster"
            authContext={authContext}
            actionButton={
              organize
                ? [
                    {
                      props: {
                        variant: "contained",
                        color: "primary",
                        onClick: (e) => {
                          if (event?.teamEvent) {
                            setTeamPlayer({});
                          } else {
                            setPlayer({});
                          }
                        },
                        startIcon: <BCPSquarePlus />
                      },
                      label: event?.teamEvent ? t("addTeam") : t("addPlayer")
                    },
                    {
                      props: {
                        variant: "outlined",
                        color: "primary",
                        "aria-expanded": Boolean(optionsAnchorEl)
                          ? true
                          : undefined,
                        onClick: (e) => {
                          setOptionsAnchorEl(e.currentTarget);
                        }
                      },
                      label: t("options")
                    },
                    {
                      props: {
                        variant: "contained",
                        color: "primary",
                        "aria-expanded": Boolean(optionsAnchorEl)
                          ? true
                          : undefined,
                        onClick: (e) => {
                          setFiltersAnchorEl(e.currentTarget);
                        },
                        endIcon: filteredPlayers ? (
                          <BCPFilter sx={{ fontSize: "20px", width: "20px" }} />
                        ) : (
                          <BCPFilterSlash
                            sx={{ fontSize: "24px", width: "24px" }}
                          />
                        )
                      },
                      label: t("filters")
                    }
                  ]
                : null
            }
            searchProps={getSearchProps(Boolean(event?.teamEvent), t)}
            rowProps={{
              mapFunction: (row, index) => (
                <>
                  {event?.teamEvent ? (
                    <TeamRow
                      row={row}
                      event={event}
                      setTeamPlayer={setTeamPlayer}
                      setPlayer={setPlayer}
                      organize={organize}
                      t={t}
                    />
                  ) : (
                    <PlayerRow
                      row={row}
                      event={event}
                      setPlayer={setPlayer}
                      organize={organize}
                      t={t}
                    />
                  )}
                </>
              )
            }}
          />
        </Grid>
      </Grid>

      {organize && (
        <>
          <OptionsMenu
            anchorEl={optionsAnchorEl}
            setAnchorEl={setOptionsAnchorEl}
            setLoading={setLoading}
            event={event}
            authContext={authContext}
            setBulkAddOpen={setBulkAddOpen}
            setBulkAddEmailsOpen={setBulkAddEmailsOpen}
            players={players}
            setRefreshPlayers={setRefreshPlayers}
            t={t}
          />

          <FiltersMenu
            anchorEl={filtersAnchorEl}
            setAnchorEl={setFiltersAnchorEl}
            event={event}
            players={players}
            setFilteredPlayers={setFilteredPlayers}
            filterType={filterType}
            setFilterType={setFilterType}
            t={t}
          />

          <OrganizePlayerModal
            organize={organize}
            player={player}
            setPlayer={setPlayer}
            event={event}
            armies={armies}
            setRefreshPlayers={setRefreshPlayers}
            deletePlayer={deletePlayer}
          />

          <OrganizeTeamPlayerModal
            teamPlayer={teamPlayer}
            setTeamPlayer={setTeamPlayer}
            event={event}
            armies={armies}
            setRefreshPlayers={setRefreshPlayers}
          />

          <BulkAddPlayerModal
            open={bulkAddOpen}
            setOpen={setBulkAddOpen}
            event={event}
            armies={armies}
            setRefreshPlayers={setRefreshPlayers}
          />

          <BulkAddPlayerEmailsModal
            open={bulkAddEmailsOpen}
            setOpen={setBulkAddEmailsOpen}
            event={event}
            armies={armies}
            setRefreshPlayers={setRefreshPlayers}
          />
        </>
      )}
      {!organize && (
        <>
          <PlayerModal
            player={player}
            setPlayer={setPlayer}
            event={event}
            authContext={authContext}
          />
        </>
      )}

      {loading && <ModalLoadingFull />}
    </Container>
  );
}

const TeamRow = (props) => {
  const { row, event, setTeamPlayer, setPlayer, organize, t } = props;

  return (
    <Grid
      item
      xs={12}
      sx={{
        mb: 1,
        border: "1px solid rgba(0, 0, 0, 0.12)",
        borderRadius: 1,
        boxShadow: 1
      }}
    >
      <Box
        onClick={(e) => {
          setTeamPlayer(row);
        }}
        sx={{
          p: 1,
          mb: 1,
          border: "1px solid rgba(0, 0, 0, 0.12)",
          borderTopLeftRadius: "4px",
          borderTopRightRadius: "4px",
          boxShadow: 1,
          "&:hover": {
            cursor: "pointer"
          },

          backgroundColor: "#545b62",
          color: "#fff"
        }}
      >
        <Grid container>
          <Grid item xs={12} sm={8} lg={9}>
            <Typography variant="body1" sx={{ overflowX: "hidden" }}>
              {row?.name}
            </Typography>

            <Typography
              variant="caption"
              paragraph
              sx={{ mb: 0, fontStyle: "italic" }}
            >
              {t("teamCaptain")}{" "}
              {userDisplayName(
                row?.teamCaptain?.firstName || row?.captain?.firstName,
                row?.teamCaptain?.lastName || row?.captain?.lastName
              )}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4} lg={3} sx={{ textAlign: "right" }}>
            <Chip
              label={
                row?.dropped
                  ? t("dropped")
                  : row?.checkedIn
                    ? t("checkedIn")
                    : t("notCheckedIn")
              }
              size="small"
              sx={{ width: "140px" }}
              color={
                row?.dropped ? "error" : row?.checkedIn ? "success" : "warning"
              }
            />
          </Grid>
        </Grid>
      </Box>

      {row?.players?.length > 0 &&
        row?.players.map((player, index) => {
          return (
            <PlayerRow
              key={index}
              index={index}
              row={player}
              event={event}
              setPlayer={setPlayer}
              organize={organize}
              team
              t={t}
            />
          );
        })}
    </Grid>
  );
};

const PlayerRow = (props) => {
  const { row, event, setPlayer, organize, team, t } = props;

  if (!row?.user?.id) {
    return <></>;
  }
  return (
    <Grid
      item
      xs={12}
      onClick={
        event?.numberOfRounds > 0 || organize
          ? () => {
              setPlayer(row);
            }
          : null
      }
      sx={
        event?.numberOfRounds > 0 || organize
          ? {
              "&:hover": {
                cursor: "pointer"
              }
            }
          : null
      }
    >
      {getEnvironmentVariable("COMPANY_FLAG") === "solforge" ? (
        <Grid container sx={{ m: 1 }}>
          <Grid item>
            <Avatar
              alt={`${row?.user?.firstName} ${row?.user?.lastName}`}
              src={row?.user?.profileImageUrl}
              sx={{ width: 50, height: 50, mr: 1 }}
            />
          </Grid>
          <Grid
            item
            sx={{
              flex: 1
            }}
          >
            <Typography variant="body1">
              {userDisplayName(
                row?.firstName || row?.user?.firstName,
                row?.lastName || row?.user?.lastName
              )}
              {row?.team?.name || row?.teamName
                ? ` - ${row?.team?.name || row?.teamName}`
                : ""}
            </Typography>
            {(organize || !event.hideLists) && (
              <Typography
                variant="caption"
                paragraph
                sx={{
                  mb: 0,
                  fontStyle: "italic"
                }}
              >
                {getEnvironmentVariable("COMPANY_FLAG") === "solforge" ? (
                  <>
                    {row?.armyListSolforge?.myDecks?.length > 0 && (
                      <>
                        {row?.armyListSolforge.myDecks
                          .map((deck) => deck.name)
                          .join(" - ")}
                      </>
                    )}
                  </>
                ) : (
                  <>
                    {IS_WARMACHINE && row?.faction?.parentFaction?.name
                      ? `${row?.faction?.parentFaction?.name} - `
                      : ""}
                    {row?.army?.name || row?.faction?.name}
                    {row?.character?.name || row?.characterName
                      ? ` - ${row?.character.name || row?.characterName}`
                      : row?.subFaction?.name
                        ? ` - ${row?.subFaction.name}`
                        : ""}
                  </>
                )}
              </Typography>
            )}
          </Grid>
          <Grid
            item
            sx={{
              textAlign: "right",
              maxWidth: ["100%", "100%", "33.333333%", "25%"],
              minWidth: ["100%", "100%", "33.333333%", "25%"]
            }}
          >
            <Chip
              label={
                row?.dropped
                  ? t("dropped")
                  : row?.checkedIn
                    ? t("checkedIn")
                    : t("notCheckedIn")
              }
              size="small"
              sx={{ width: "140px" }}
              color={
                row?.dropped ? "error" : row?.checkedIn ? "success" : "warning"
              }
            />

            {(organize || !event.hideLists) && (
              <ArmyListIconButton
                organize={organize}
                event={event}
                player={row}
                dense
              />
            )}
          </Grid>
        </Grid>
      ) : (
        <Grid container sx={{ m: 1 }}>
          <Hidden mdUp>
            <Grid
              item
              sx={{
                flex: 1
              }}
            >
              <Typography variant="body1" gutterBottom>
                {row?.firstName || row?.user?.firstName}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {row?.lastName || row?.user?.lastName}
              </Typography>
              {row?.extendedGroupName && (
                <Typography variant="body1" gutterBottom>
                  {row?.extendedGroupName}
                </Typography>
              )}

              {(row?.team?.name || row?.teamName) &&
                (row?.team?.name || row?.teamName) !==
                  row?.extendedGroupName && (
                  <>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      {t("teamName")}
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {row?.team?.name || row?.teamName}
                    </Typography>
                  </>
                )}

              {(organize || !event.hideLists) && (
                <Typography
                  variant="body1"
                  gutterBottom
                  sx={{
                    fontStyle: "italic"
                  }}
                >
                  {getEnvironmentVariable("COMPANY_FLAG") === "solforge" ? (
                    <>
                      {row?.armyListSolforge?.myDecks?.length > 0 && (
                        <>
                          {row?.armyListSolforge.myDecks
                            .map((deck) => deck.name)
                            .join(" - ")}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {IS_WARMACHINE && row?.faction?.parentFaction?.name
                        ? `${row?.faction?.parentFaction?.name} - `
                        : ""}
                      {row?.army?.name || row?.faction?.name}
                      {row?.character?.name || row?.characterName
                        ? ` - ${row?.character?.name || row?.characterName}`
                        : row?.subFaction?.name || row?.subFactionName
                          ? ` - ${row?.subFaction?.name || row?.subFactionName}`
                          : ""}
                    </>
                  )}
                </Typography>
              )}

              {(organize || !event.hideLists) && (
                <Typography variant="body1" gutterBottom>
                  {organize && row.fixedTable > 0 && (
                    <IconButton sx={{ mr: 1 }}>
                      <BCPChair />
                    </IconButton>
                  )}
                  <Chip
                    label={
                      row?.dropped
                        ? t("dropped")
                        : row?.checkedIn
                          ? t("checkedIn")
                          : t("notCheckedIn")
                    }
                    size="small"
                    sx={{ width: "140px", mr: 1 }}
                    color={
                      row?.dropped
                        ? "error"
                        : row?.checkedIn
                          ? "success"
                          : "warning"
                    }
                  />
                  <ArmyListLink
                    organize={organize}
                    event={event}
                    player={row}
                    dense
                  />
                </Typography>
              )}
            </Grid>
          </Hidden>
          <Hidden mdDown>
            <Grid
              item
              sx={{
                flex: 1
              }}
            >
              <Typography variant="body1">
                {userDisplayName(
                  row?.firstName || row?.user?.firstName,
                  row?.lastName || row?.user?.lastName
                )}
                {row?.extendedGroupName
                  ? ` - ${row?.extendedGroupName}`
                  : row?.team?.name || row?.teamName
                    ? ` - ${row?.team?.name || row?.teamName}`
                    : ""}
              </Typography>
              {row?.extendedGroupName &&
                (row?.team?.name || row?.teamName) &&
                row?.extendedGroupName !==
                  (row?.team?.name || row?.teamName) && (
                  <Typography variant="body1">
                    {row?.team?.name || row?.teamName}
                  </Typography>
                )}
              {(organize || !event.hideLists) && (
                <Typography
                  variant="caption"
                  paragraph
                  sx={{
                    mb: 0,
                    fontStyle: "italic"
                  }}
                >
                  {getEnvironmentVariable("COMPANY_FLAG") === "solforge" ? (
                    <>
                      {row?.armyListSolforge?.myDecks?.length > 0 && (
                        <>
                          {row?.armyListSolforge.myDecks
                            .map((deck) => deck.name)
                            .join(" - ")}
                        </>
                      )}
                    </>
                  ) : (
                    <>
                      {IS_WARMACHINE && row?.faction?.parentFaction?.name
                        ? `${row?.faction?.parentFaction?.name} - `
                        : ""}
                      {row?.army?.name || row?.faction?.name}
                      {row?.character?.name || row?.characterName
                        ? ` - ${row?.character?.name || row?.characterName}`
                        : row?.subFaction?.name
                          ? ` - ${row?.subFaction.name}`
                          : ""}
                    </>
                  )}
                </Typography>
              )}
            </Grid>
            <Grid
              item
              sx={{
                textAlign: "right",
                maxWidth: ["100%", "100%", "33.333333%", "25%"],
                minWidth: ["100%", "100%", "33.333333%", "25%"],
                pr: !organize || event.hideLists ? 2 : ""
              }}
            >
              {organize && row.fixedTable > 0 && (
                <IconButton sx={{ mr: 1 }}>
                  <BCPChair />
                </IconButton>
              )}

              <Chip
                label={
                  row?.dropped
                    ? t("dropped")
                    : row?.checkedIn
                      ? t("checkedIn")
                      : t("notCheckedIn")
                }
                size="small"
                sx={{ width: "140px" }}
                color={
                  row?.dropped
                    ? "error"
                    : row?.checkedIn
                      ? "success"
                      : "warning"
                }
              />

              {(organize || !event.hideLists) && (
                <ArmyListIconButton
                  organize={organize}
                  event={event}
                  player={row}
                  dense
                />
              )}
            </Grid>
          </Hidden>
        </Grid>
      )}
      {!team && <Divider sx={{ mt: 1, mb: 1 }} />}
    </Grid>
  );
};
